import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

import { CoreContext } from "context/CoreContext";
import { ReadLearningPaths } from "services/learning-paths";
import { ReadDrawerMenu } from "services/sideMenu";
import { captureSessionState, isSessionStateCurrent } from "services/session";
import { normalizePortal } from "utils/portal";
import { apiRequest, exposeStrapiError } from "utils";
import { languages } from "utils/options";

const EMPTY_MENU = [];

function isLearningPathsItem(item) {
    const actionValue = `${item?.action?.value || ''}`;
    const key = `${item?.key || ''}`;

    return key.includes('knowledge-trails') || /(^|\/)trails($|[/?#])/.test(actionValue);
}

function filterLearningPathsItems(items, hasLearningPaths) {
    if (hasLearningPaths) return items;

    return (items || [])
        .filter((item) => !isLearningPathsItem(item))
        .map((item) => (
            Array.isArray(item?.children)
                ? { ...item, children: filterLearningPathsItems(item.children, hasLearningPaths) }
                : item
        ));
}

export default function useDrawerMenu(portal) {
    const { user, language } = useContext(CoreContext);
    const location = useLocation();
    const [result, setResult] = useState({ scope: null, menu: EMPTY_MENU, loading: false, loaded: false });
    const requestId = useRef(0);
    const cancelPending = useCallback(() => { requestId.current++; }, []);

    const resolvedPortal = useMemo(() => {
        if (portal) return normalizePortal(portal);
        if (location?.pathname?.startsWith('/mvu-accelerator')) return 'accelerator';
        if (location?.pathname?.startsWith('/mvu-lite') || location?.pathname?.startsWith('/mvu-essentials')) return 'essentials';
        return 'universe';
    }, [location?.pathname, portal]);
    const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || 'en', [language]);
    const userId = user?.id;
    const profileReady = Boolean(userId && (user?.profileAuthority !== 'profile'
        || user.userProfile?.area === (resolvedPortal === 'essentials' ? 'lite' : resolvedPortal)));
    const scope = useMemo(() => JSON.stringify([
        userId, resolvedPortal, locale, user?.userProfile?.area,
        user?.userProfile?.revision, user?.userProfile?.status, user?.userProfile?.enabled,
        user?.userProfile?.role, user?.role, user?.grant_access,
        user?.blocked, user?.confirmed, user?.mvu_light_access,
        user?.mvu_accelerator_access, user?.can_approve_accelerator_certificates,
        (user?.userProfile?.tags || user?.tags || []).map(tag => [tag.id, tag.name]).sort(),
    ]), [locale, resolvedPortal, user, userId]);

    const load = useCallback(async () => {
        if (!profileReady) return;
        const currentRequest = ++requestId.current;
        const session = captureSessionState();
        const isCurrent = () => requestId.current === currentRequest && isSessionStateCurrent(session);
        setResult(current => ({
            scope,
            menu: current.scope === scope ? current.menu : EMPTY_MENU,
            loaded: current.scope === scope && current.loaded,
            loading: true,
        }));

        const area = ['essentials', 'accelerator'].includes(resolvedPortal) ? resolvedPortal : '';
        try {
            // Learning Paths is optional navigation. Its failure or delay must
            // not block the authoritative staff invitation menu.
            const trails = Promise.resolve()
                .then(() => apiRequest(() => ReadLearningPaths({ portal: resolvedPortal, locale })))
                .catch(() => null);
            const menuResult = await apiRequest(() => ReadDrawerMenu(area, locale));
            if (!isCurrent()) return;
            if (exposeStrapiError(menuResult)) {
                const denied = [401, 403].includes(menuResult?.error?.status);
                setResult(current => ({
                    scope, loading: false, loaded: true,
                    menu: !denied && current.scope === scope ? current.menu : EMPTY_MENU,
                }));
                return;
            }
            const drawerMenu = Array.isArray(menuResult?.data) ? menuResult.data : [];
            setResult({ scope, menu: drawerMenu, loading: false, loaded: true });

            const trailsResult = await trails;
            if (!isCurrent() || !trailsResult || exposeStrapiError(trailsResult)) return;
            const hasLearningPaths = Array.isArray(trailsResult.data) && trailsResult.data.length > 0;
            setResult({ scope, menu: filterLearningPathsItems(drawerMenu, hasLearningPaths), loading: false, loaded: true });
        } catch (error) {
            if (!isCurrent()) return;
            exposeStrapiError(false);
            setResult(current => ({
                scope, loading: false, loaded: true,
                menu: current.scope === scope ? current.menu : EMPTY_MENU,
            }));
        }
    }, [locale, profileReady, resolvedPortal, scope]);

    useEffect(() => {
        if (!profileReady) return;
        load();
        // The authenticated layout persists between pages. Only a new account,
        // area, language or profile scope invalidates its in-memory menu.
        return cancelPending;
    }, [cancelPending, load, profileReady]);

    return {
        menu: profileReady && result.scope === scope ? result.menu : EMPTY_MENU,
        loading: Boolean(userId && (!profileReady || result.scope !== scope || (result.loading && !result.loaded))),
        refreshing: Boolean(profileReady && result.scope === scope && result.loading && result.loaded),
        reload: load,
    };
}
